import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDebounce } from "usehooks-ts";
import DeleteIcon from "../../components/icons/DeleteIcon";
import PromptModal from "../../components/modals/PromptModal";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";

import { apiConfig } from "../../config/apiConfig";
import { colors } from "../../config/theme";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import CreatePartnerAccountModal from "./CreatePartnerAccountModal";
import { tdStyle, thStyle } from "../../helpers/constants";
import { Chip, Dash, IconAction } from "../../helpers/general";
import { cardStyle } from "../Places/PlacesTable";
import SearchBox from "../../components/ui/SearchBox";

const PAGE_SIZE = 10;

const sourceLabel = (source?: PartnerRequest["source"]) =>
  source === "partners" ? "Partners" : source === "web" ? "Venez web" : "—";

function RequestRow({
  request,
  onCreateAccount,
  onDelete,
}: {
  request: PartnerRequest;
  onCreateAccount: () => void;
  onDelete: () => void;
}) {
  const accountCreated = Boolean(
    request.accountCreated || request.convertedUserId,
  );

  return (
    <CTableRow>
      <CTableDataCell style={tdStyle}>
        <div className="fw-semibold text-capitalize">{request.fullName}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {request.email}
        </div>
        {request.phone && (
          <div
            className="text-muted"
            style={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {request.phone}
          </div>
        )}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {request.businessName ? (
          <div className="fw-semibold">{request.businessName}</div>
        ) : (
          <Dash />
        )}
        {request.city?.label && (
          <div className="text-muted" style={{ fontSize: 12 }}>
            {request.city.label}
          </div>
        )}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <Chip tone={request.source === "partners" ? "warning" : "neutral"}>
          {sourceLabel(request.source)}
        </Chip>
      </CTableDataCell>

      <CTableDataCell style={{ ...tdStyle, whiteSpace: "nowrap" }}>
        {format(new Date(request.createdAt), "dd/MM/yyyy")}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          {request.source === "partners" &&
            (accountCreated ? (
              <Chip tone="success">✓ Compte créé</Chip>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-primary text-white rounded-pill px-3"
                onClick={onCreateAccount}
              >
                Créer le compte
              </button>
            ))}
          {request.source === "web" && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary rounded-pill px-3"
              onClick={() =>
                window.open("/places/new", "_blank", "noopener,noreferrer")
              }
            >
              Créer l'établissement
            </button>
          )}
          <IconAction title="Supprimer" danger onClick={onDelete}>
            <DeleteIcon width="18" height="18" fill={colors.primary} />
          </IconAction>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

export default function PartnerRequests() {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [toDelete, setToDelete] = useState<PartnerRequest | null>(null);
  const [creating, setCreating] = useState<PartnerRequest | null>(null);

  const search = useDebounce(searchQuery, 500);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const filter = useMemo(
    () => ({ page: currentPage, limit: PAGE_SIZE, query: search }),
    [currentPage, search],
  );

  const { data, isLoading } = useQuery(
    ["getPartnersRequest", filter],
    () => apiService.MakeGetRequest(apiConfig.partnair.request(filter), token),
    { keepPreviousData: true },
  );

  const deleteMutation = useMutation((id: string) =>
    apiService.MakeDeleteRequest(`partners/requests/${id}`, token),
  );

  const confirmDelete = () => {
    if (!toDelete) return;
    deleteMutation.mutate(toDelete.id, {
      onError: () => toastError(general.fr.message.operationFailed),
      onSuccess() {
        queryClient.invalidateQueries("getPartnersRequest");
        toastSuccess(general.fr.message.partnerDeleted);
      },
    });
    setToDelete(null);
  };

  const items: PartnerRequest[] = data?.items ?? [];

  return (
    <div>
      <div
        style={{ ...cardStyle, padding: "12px 16px" }}
        className="d-flex flex-wrap align-items-center gap-2 mb-3"
      >
        <SearchBox
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Rechercher une demande"
        />
        {data?.meta?.totalItems !== undefined && (
          <Chip tone="warning">
            {data.meta.totalItems}{" "}
            {data.meta.totalItems > 1 ? "demandes" : "demande"}
          </Chip>
        )}
      </div>

      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isLoading}
          render={() =>
            items.length > 0 ? (
              <CTable responsive hover align="middle" className="mb-0">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell style={thStyle}>Contact</CTableHeaderCell>
                    <CTableHeaderCell style={thStyle}>
                      Entreprise
                    </CTableHeaderCell>
                    <CTableHeaderCell style={thStyle}>Source</CTableHeaderCell>
                    <CTableHeaderCell style={thStyle}>Date</CTableHeaderCell>
                    <CTableHeaderCell
                      style={{ ...thStyle, textAlign: "right" }}
                    >
                      Actions
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {items.map((request) => (
                    <RequestRow
                      key={request.id}
                      request={request}
                      onCreateAccount={() => setCreating(request)}
                      onDelete={() => setToDelete(request)}
                    />
                  ))}
                </CTableBody>
              </CTable>
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        siblingCount={1}
        totalCount={data?.meta?.totalPages}
        pageSize={PAGE_SIZE}
      />

      <PromptModal
        open={toDelete !== null}
        onAccept={confirmDelete}
        onClose={() => setToDelete(null)}
      />

      {creating && (
        <CreatePartnerAccountModal
          request={creating}
          open
          onClose={() => setCreating(null)}
        />
      )}
    </div>
  );
}
